import React, { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import useProyecto from "../hooks/useProyecto";
import { ReportesContext } from "../context/ReportesContext";
import { MenuContext } from "../context/MenuContext";
import { PartidasContext } from "../context/PartidasContext";
import ReporteHeader from "../components/reportes/ReporteHeader";
import ReporteGantt from "../components/reportes/ReporteGantt";
import ReporteContenido from "../components/reportes/ReporteContenido";
import ReporteAvanceBars from "../components/reportes/ReporteAvanceBars";
import ReporteGanttPartidas from "../components/reportes/ReporteGanttPartidas";
import ReporteIndicadores from "../components/reportes/ReporteIndicadores";

const SingleReporte = () => {
  const { proyecto, idProyecto, fetchProyecto, permiso } = useProyecto();
  const { idReporte } = useParams();

  const {
    reporte,
    generandoPDF,
    getSingleReporte,
    abrirReportePDF,
    regenerarReportePDF,
  } = useContext(ReportesContext);
  const { partidas, getPartidasProyecto } = useContext(PartidasContext);
  const { setSelected } = useContext(MenuContext);

  // The obra name as the page heading.
  useEffect(() => {
    setSelected({ title: "reportes", heading: proyecto?.nombre });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proyecto?.nombre]);

  useEffect(() => {
    fetchProyecto(idProyecto);
    getSingleReporte(idProyecto, idReporte);
    if (idProyecto) getPartidasProyecto(idProyecto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idProyecto, idReporte]);

  // Browsers name the printed PDF after document.title:
  // Reporte_<idReporte>_<NombreProyecto>.pdf
  useEffect(() => {
    if (!reporte?.idReporte) return;
    const nombreProyecto = String(proyecto?.nombre || "")
      .trim()
      .replace(/[\\/:*?"<>|]+/g, "-")
      .replace(/\s+/g, "_");
    const previousTitle = document.title;
    document.title = ["Reporte", reporte.idReporte, nombreProyecto]
      .filter(Boolean)
      .join("_");
    return () => {
      document.title = previousTitle;
    };
  }, [reporte?.idReporte, proyecto?.nombre]);


  // The server checks the saved PDF still exists in S3 and generates it again
  // when it doesn't (an old link answered "Access Denied").
  // Admins and coordinadores can print the PDF again (only the printable
  // file; the report's data stays as saved).
  const puedeRegenerar =
    ["admin", "coordinador"].includes(proyecto?.permiso) ||
    permiso === "admin";

  const handlePDF = () => abrirReportePDF(idProyecto, reporte);

  const handleRegenerar = () =>
    regenerarReportePDF(idProyecto, reporte.idReporte);

  return (
    <div className="container-fluid single-reporte-print">
      <style>{`
        .gantt-fit-wrap { width: 100%; overflow-x: auto; }
        .gantt-fit-wrap .gantt-scroll { width: 100%; }
        @media print {
          @page { size: A4; margin: 10mm; }
          html, body {
            background: #fff !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          body * { visibility: hidden !important; }
          .single-reporte-print, .single-reporte-print * { visibility: visible !important; }
          .single-reporte-print {
            position: absolute;
            inset: 0;
            margin: 0;
            padding: 0;
            background: #fff !important;
          }
          .single-reporte-print .no-print { display: none !important; }
          .single-reporte-print .card {
            box-shadow: none !important;
            border: none !important;
            background: #fff !important;
            padding: 0 !important;
          }
          .single-reporte-print {
            font-size: 10px;
          }
          .single-reporte-print h1,
          .single-reporte-print h2,
          .single-reporte-print h3,
          .single-reporte-print h4,
          .single-reporte-print h5 { font-size: 12px !important; margin-bottom: 4px !important; }
          .single-reporte-print .table { font-size: 9px !important; }
          .single-reporte-print .table td,
          .single-reporte-print .table th { padding: 2px 4px !important; }
          .single-reporte-print img,
          .single-reporte-print .adjunto-mensaje {
            max-width: 80px !important;
            max-height: 80px !important;
            page-break-inside: avoid;
          }
          .single-reporte-print .row { page-break-inside: avoid; }
          .single-reporte-print .gantt-fit-wrap {
            overflow: hidden !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          .single-reporte-print .gantt-fit-wrap svg,
          .single-reporte-print .gantt-fit-wrap canvas {
            max-width: 100% !important;
            height: auto !important;
          }
          .single-reporte-print .gantt-fit-wrap .gantt-scroll,
          .single-reporte-print .gantt-fit-wrap .gantt-scroll > div {
            width: 100% !important;
            max-width: 100% !important;
            overflow: hidden !important;
          }
          .single-reporte-print .gantt-fit-wrap > div > div {
            zoom: 0.46;
          }
          .single-reporte-print .row.print-half-row {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            align-items: center !important;
          }
          .single-reporte-print .row.print-half-row > [class*="col-"] {
            flex: 0 0 50% !important;
            max-width: 50% !important;
            width: 50% !important;
          }
        }
      `}</style>
      <div id="report-header" className="row mb-4 align-items-center no-print">
        <div className="col">
          <h1 className="h2">Reporte</h1>
        </div>
        <div className="col-auto text-end text-nowrap">
          {puedeRegenerar && (
            <button
              className="btn btn-outline-primary me-2"
              onClick={handleRegenerar}
              disabled={Boolean(generandoPDF)}
              title="Regenerar PDF de impresión"
            >
              {generandoPDF === "regenerar" ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                <i className="fa fa-sync-alt" />
              )}
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={handlePDF}
            disabled={Boolean(generandoPDF)}
            title={generandoPDF ? "Generando PDF..." : "Imprimir / PDF"}
          >
            {generandoPDF === "imprimir" ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <i className="fa fa-print" />
            )}
          </button>
        </div>
      </div>
      <div className="card p-3 shadow">
        <div className="row align-items-center print-half-row">
          <div className="col-12 col-md-6">
            <ReporteAvanceBars
              programado={reporte?.programado}
              real={reporte?.real}
              financiero={reporte?.indicadores?.financiero}
            />
          </div>
          <div className="col-12 col-md-6">
            <ReporteHeader reporte={reporte} />
          </div>
        </div>
        <ReporteGantt reporte={reporte} />
        <ReporteContenido seccion="resumen" reporte={reporte} />
        {reporte?.idReporte && (
          <ReporteIndicadores
            idProyecto={idProyecto}
            fecha={reporte.fecha_fin}
            indicadores={reporte.indicadores}
            incluirFinanciero={reporte.indicadores?.incluir_financiero !== false}
            incluirFechaTerminacion={
              reporte.indicadores?.incluir_fecha_terminacion !== false
            }
          />
        )}
        {Array.isArray(partidas) && partidas.length > 0 && (
          <div className="mt-3 mb-4">
            <ReporteGanttPartidas
                partidas={partidas.filter((p) => Number(p.programado) > 0)}
                resumenes={reporte?.indicadores?.resumen_partidas}
              />
          </div>
        )}
        <ReporteContenido seccion="imagenes" reporte={reporte} />
      </div>
    </div>
  );
};

export default SingleReporte;
